import { TICK_MS } from "@pd/shared";
import type { Observation, WizardOrbWave } from "@pd/shared";
import type { WorldObject } from "./actions.js";
import { HALF_H, HALF_W, tileToScreen } from "./iso.js";

export const ORB_LIFT = 20;
export const ORB_RADIUS = 13;

/** Party observations can contain several encounters; show only the local room's cue. */
export function wizardWaveForPlayer(obs: Observation, meId: number): WizardOrbWave | null {
  const me = obs.players.find((p) => p.id === meId);
  if (!me?.alive) return null;
  const stride = obs.combat.roomTiles + 1;
  for (const enemy of obs.enemies) {
    const wave = enemy.orbWave;
    if (!enemy.alive || !wave || wave.startsAt < 0) continue;
    const puzzle = obs.puzzles?.find((p) => p.kind === "orb-wizard" && p.enemyId === enemy.id);
    const sameRoom = puzzle?.kind === "orb-wizard"
      ? me.x >= puzzle.x - 1 && me.x < puzzle.x + puzzle.side && me.y >= puzzle.y - 1 && me.y < puzzle.y + puzzle.side
      : obs.returnPortal === null || (Math.floor((me.x + 1) / stride) === Math.floor((enemy.x + 1) / stride) && Math.floor((me.y + 1) / stride) === Math.floor((enemy.y + 1) / stride));
    if (sameRoom) return wave;
  }
  return null;
}

/** Screen-space presentation of authoritative ticks; it never advances a wave. */
export function drawWizardWaveHud(ctx: CanvasRenderingContext2D, obs: Observation, meId: number, tick: number, canvasWidth: number): void {
  const wave = wizardWaveForPlayer(obs, meId);
  if (!wave) return;
  const width = Math.min(280, canvasWidth - 24), height = 108;
  const x = (canvasWidth - width) / 2, y = 18, cx = canvasWidth / 2;
  const countdown = wave.expiresAt === 0;
  const remaining = Math.max(0, wave.expiresAt - tick);
  const fraction = Math.min(1, remaining / Math.max(1, wave.expiresAt - wave.startsAt));
  const accent = !countdown && fraction < 0.3 ? "#ffb16d" : "#f3ddff";
  ctx.save();
  ctx.fillStyle = "rgba(20, 14, 31, 0.9)";
  ctx.strokeStyle = "#785599";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.roundRect(x, y, width, height, 12); ctx.fill(); ctx.stroke();
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillStyle = "#cbb4df";
  ctx.font = "600 14px system-ui, sans-serif";
  ctx.fillText(countdown ? "ORBS IN" : "BURST THE ORBS", cx, y + 23);
  ctx.fillStyle = accent;
  if (countdown) {
    ctx.font = "700 54px system-ui, sans-serif";
    ctx.fillText(String(Math.max(1, Math.ceil((wave.startsAt - tick) * TICK_MS / 1000))), cx, y + 69);
  } else {
    ctx.font = "600 24px system-ui, sans-serif";
    ctx.fillText(`${wave.clicked} / ${wave.clicked + wave.orbs.length}  ·  ${(remaining * TICK_MS / 1000).toFixed(1)}s`, cx, y + 56);
    ctx.fillStyle = "#43304f";
    ctx.fillRect(x + 20, y + 84, width - 40, 8);
    ctx.fillStyle = accent;
    ctx.fillRect(x + 20, y + 84, (width - 40) * fraction, 8);
  }
  ctx.restore();
}

/** Match the hovering object, including a generous visible ring, before NPC bodies. */
export function wizardOrbAtScreenPoint(obs: Observation, sx: number, sy: number, unit: number, camX: number, camY: number): Extract<WorldObject, { kind: "wizardOrb" }> | null {
  // The lifted orb remains directly clickable, including its glow and ring.
  for (const enemy of obs.enemies) for (const orb of enemy.alive ? enemy.orbWave?.orbs ?? [] : []) {
    const p = tileToScreen(orb.x, orb.y, unit, camX, camY);
    const dx = sx - p.x, dy = sy - p.y - (HALF_H - ORB_LIFT) * unit;
    if (dx * dx + dy * dy <= (ORB_RADIUS + 7) * (ORB_RADIUS + 7) * unit * unit) return { kind: "wizardOrb", ...orb };
  }
  // The orb also owns its complete floor diamond, so the click need not land
  // on the raised sprite. Use the rendered diamond edges so its boundary is
  // included even where the inverse tile mapper has to break a shared edge.
  for (const enemy of obs.enemies) for (const orb of enemy.alive ? enemy.orbWave?.orbs ?? [] : []) {
    const p = tileToScreen(orb.x, orb.y, unit, camX, camY);
    const dx = Math.abs(sx - p.x) / (HALF_W * unit);
    const dy = Math.abs(sy - (p.y + HALF_H * unit)) / (HALF_H * unit);
    if (dx + dy <= 1) return { kind: "wizardOrb", ...orb };
  }
  return null;
}

export function drawWizardOrb(ctx: CanvasRenderingContext2D, x: number, y: number, unit: number, remaining: number): void {
  const cy = y + (HALF_H - ORB_LIFT) * unit;
  ctx.save();
  ctx.shadowColor = "#b378ff"; ctx.shadowBlur = 15 * unit;
  ctx.fillStyle = "#a966ee";
  ctx.beginPath(); ctx.arc(x, cy, ORB_RADIUS * unit, 0, Math.PI * 2); ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = "#f3ddff";
  ctx.beginPath(); ctx.arc(x - 3 * unit, cy - 4 * unit, 5 * unit, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = "#55416e"; ctx.lineWidth = 3 * unit;
  ctx.beginPath(); ctx.arc(x, cy, (ORB_RADIUS + 6) * unit, 0, Math.PI * 2); ctx.stroke();
  ctx.strokeStyle = remaining < 0.3 ? "#ffb16d" : "#f8dbff";
  ctx.beginPath(); ctx.arc(x, cy, (ORB_RADIUS + 6) * unit, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.max(0, remaining)); ctx.stroke();
  ctx.restore();
}
